import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { MenuItemWithTranslations, AddOn } from '../types/menu';
import { Plus, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface MenuCategoryProps {
  items: MenuItemWithTranslations[];
}

const MenuCategory: React.FC<MenuCategoryProps> = ({ items }) => {
  const { addToCart } = useCart();
  const { language, t } = useLanguage();
  const [animatingItems, setAnimatingItems] = useState<{ [key: string]: boolean }>({});
  const [selectedAddOns, setSelectedAddOns] = useState<{[key: string]: AddOn[]}>({});
  const confirmTimeouts = React.useRef<{ [key: string]: number }>({});

  // Clean up any pending confirmation timers on unmount
  useEffect(() => {
    const timeouts = confirmTimeouts.current;
    return () => {
      Object.values(timeouts).forEach((id) => window.clearTimeout(id));
    };
  }, []);

  const handleAddToCart = (item: MenuItemWithTranslations) => {
    const itemAddOns = selectedAddOns[item.id] || [];

    setAnimatingItems(prev => ({ ...prev, [item.id]: true }));

    addToCart({
      ...item,
      addOns: itemAddOns,
      translations: item.translations
    });

    window.clearTimeout(confirmTimeouts.current[item.id]);
    confirmTimeouts.current[item.id] = window.setTimeout(() => {
      setAnimatingItems(prev => ({ ...prev, [item.id]: false }));
      setSelectedAddOns(prev => ({ ...prev, [item.id]: [] }));
      delete confirmTimeouts.current[item.id];
    }, 1500);
  };

  const toggleAddOn = (itemId: string, addon: AddOn) => {
    setSelectedAddOns(prev => {
      const current = prev[itemId] || [];
      const exists = current.some(a => a.id === addon.id);
      const updated = exists
        ? current.filter(a => a.id !== addon.id)
        : [...current, addon];
      return { ...prev, [itemId]: updated };
    });
  };

  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {items.map((item) => (
        <div 
          key={item.id}
          id={`item-${item.id}`}
          className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] overflow-hidden flex flex-col transition-all duration-200 hover:shadow-[0_10px_28px_rgba(0,0,0,0.12)] hover:-translate-y-1"
        >
          {item.image && (
            <div className="relative w-full aspect-[16/9] overflow-hidden bg-white">
              <img
                src={item.image}
                alt={item.translations[language].name}
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center'
                }}
              />
            </div>
          )}
          <div className="p-3 flex-1 flex flex-col">
            <div className="flex justify-between items-start mb-1 gap-2">
              <h3 className="text-sm font-semibold text-stone-800 leading-snug">
                {item.translations[language].name}
              </h3>
              <span className="font-semibold text-[#D97706] bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap text-xs">
                {item.price} DH
              </span>
            </div>
            <p className="text-xs text-stone-500 mb-2 line-clamp-2">
              {item.translations[language].description}
            </p>

            {item.addOns && item.addOns.length > 0 && (
              <div className="mb-2 mt-auto">
                <h4 className="text-xs font-semibold text-stone-700 mb-1">
                  {t('common.addOns')}:
                </h4>
                <div className="flex flex-wrap gap-1">
                  {item.addOns.map((addon) => (
                    <button
                      key={addon.id}
                      onClick={() => toggleAddOn(item.id, addon)}
                      aria-pressed={selectedAddOns[item.id]?.some(a => a.id === addon.id) || false}
                      className={`
                        px-2 py-1 rounded-full text-xs font-medium min-h-[28px]
                        transition-colors duration-200
                        ${selectedAddOns[item.id]?.some(a => a.id === addon.id)
                          ? 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white'
                          : 'bg-amber-50 text-[#B45309] hover:bg-amber-100'
                        }
                      `}
                    >
                      {addon.name} (+{addon.price}DH)
                    </button>
                  ))}
                </div>
              </div>
            )}

            <motion.button
              onClick={() => handleAddToCart(item)}
              className={`
                w-full py-2 px-3 rounded-full transition-all duration-300
                flex items-center justify-center gap-1.5 font-body text-xs font-semibold
                mt-auto shadow-sm
                ${animatingItems[item.id]
                  ? 'bg-green-500 text-white'
                  : 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white hover:from-[#F59E0B] hover:to-[#EA580C]'
                }
              `}
              disabled={animatingItems[item.id]}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {animatingItems[item.id] ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    <span>{t('common.addToCart')}</span>
                  </>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MenuCategory;